import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import * as v from 'valibot';
import { useAppDispatch } from '../hooks';
import { TopMenu } from '../components/TopMenu';
import { isDeviceStates, updateDeviceStates } from '../reducers/device_states';
import { setDefinitions } from '../actions/SetDefinitions';
import { zigbeeAttributeUpdated } from '../features/zigbee/ZigbeeAttributesSlice';
import { attributeSchema } from '../types/Zigbee/Attribute';
import { DebugPanel } from './DebugPanel';
import { parseJSON } from '../util/json';
import { sessionID } from '../util/unique';
import { Outlet } from 'react-router-dom';
import { backendDefinitionsSchema } from '../types/Backend/Definitions';
import Ajax from '../util/ajax';
import { handleEntityStateEvent } from '../event/EntityStateEvent';
function isBackendAttribute(v) {
    return v !== null
        && typeof v === 'object'
        && 'attribute_id' in v && typeof v.attribute_id === 'number'
        && 'endpoint_id' in v && typeof v.endpoint_id === 'number'
        && 'cluster_id' in v && typeof v.cluster_id === 'number'
        && 'cluster_kind' in v && typeof v.cluster_kind === 'string' && (v.cluster_kind == 'server' || v.cluster_kind == 'client')
        && 'node_id' in v && typeof v.node_id === 'number'
        && ((!('ts' in v)) || 'ts' in v && typeof v.ts === 'string')
        && 'type' in v && typeof v.type === 'string'
        && 'value' in v;
}
export const AppView = () => {
    const dispatch = useAppDispatch();
    React.useEffect(() => {
        let api_url;
        if (process.env.NODE_ENV !== 'production') {
            //api_url = 'http://cosmo.int.zigo.org:8081/api';
            api_url = 'https://light.dev.zigo.org/api';
        }
        else {
            api_url = '/api';
        }
        let eventSource;
        let reconnect = true;
        let reconnectDelay = 1;
        let reconnectTimeoutId;
        function processAttribute(a, log_enabled) {
            if (!isBackendAttribute(a)) {
                console.error('Invalid attribute value from backend: ', a);
                return;
            }
            const attr_input = {
                attribute_id: a.attribute_id,
                changed: a.ts,
                type: a.type,
                value: a.value
            };
            const res = v.safeParse(attributeSchema, attr_input);
            if (res.success) {
                const attr = res.output;
                if (log_enabled) {
                    console.log('attr', Object.assign(Object.assign({}, attr), { nid: a.node_id, eid: a.endpoint_id, cid: a.cluster_id, kind: a.cluster_kind }));
                }
                dispatch(zigbeeAttributeUpdated(a.node_id, a.endpoint_id, a.cluster_id, attr));
            }
            else {
                console.error(v.summarize(res.issues));
            }
        }
        function startEventSource() {
            const sid = sessionID();
            eventSource = new EventSource(api_url + '/events?sid=' + sid, { withCredentials: true });
            // firefox give a warning about interrupting the events when reloading the page unless we close it when leaving the page
            window.addEventListener('beforeunload', () => { if (eventSource.readyState != EventSource.CLOSED) {
                eventSource.close();
            } });
            eventSource.addEventListener('zigbee attribute', (e) => {
                var _a;
                const data = parseJSON(e.data, 'zigbee attribute');
                if (data !== undefined) {
                    if (data !== null && typeof data == 'object') {
                        const log_enabled = ((_a = localStorage.getItem('log_event_zigbee_attributes')) !== null && _a !== void 0 ? _a : '0') == '1';
                        if (Array.isArray(data)) {
                            for (const element of data) {
                                processAttribute(element, log_enabled);
                            }
                        }
                        else {
                            processAttribute(data, log_enabled);
                        }
                    }
                    else {
                        console.error('Invalid zigbee attribute', data);
                    }
                }
            });
            eventSource.addEventListener('device states', (e) => {
                var _a;
                const data = parseJSON(e.data, 'device states');
                if (data !== undefined) {
                    if (isDeviceStates(data)) {
                        const log_enabled = ((_a = localStorage.getItem('log_event_device_states')) !== null && _a !== void 0 ? _a : '0') == '1';
                        if (log_enabled) {
                            console.log('device state', data);
                        }
                        dispatch(updateDeviceStates(data));
                    }
                    else {
                        console.error('Invalid device states', data);
                    }
                }
            });
            eventSource.addEventListener('definitions', (e) => {
                var _a;
                const data = parseJSON(e.data, 'definitions');
                if (data !== undefined) {
                    const res = v.safeParse(backendDefinitionsSchema, data);
                    if (res.success) {
                        const d = res.output;
                        dispatch(setDefinitions(d));
                        const log_enabled = ((_a = localStorage.getItem('log_event_definitions')) !== null && _a !== void 0 ? _a : '0') == '1';
                        if (log_enabled) {
                            console.log('def', d);
                        }
                    }
                    else {
                        console.error(v.summarize(res.issues));
                    }
                }
            });
            eventSource.addEventListener('entity state', (e) => handleEntityStateEvent(dispatch, e.data));
            eventSource.onopen = () => {
                reconnectDelay = 1;
            };
            eventSource.onerror = () => {
                if (reconnect && eventSource.readyState == EventSource.CLOSED) {
                    reconnectTimeoutId = setTimeout(startEventSource, reconnectDelay * 1000);
                    if (reconnectDelay < 120) {
                        reconnectDelay *= 2;
                    }
                }
            };
        }
        startEventSource();
        // Make a api call so we can get a 404 if we are not logged in which will take us to the login page..
        const ajax = new Ajax(dispatch, false);
        ajax.get('/sensors');
        return () => {
            reconnect = false;
            clearTimeout(reconnectTimeoutId);
            eventSource.close();
        };
    }, []);
    return (_jsxs(React.Fragment, { children: [_jsx(DebugPanel, {}), _jsx(TopMenu, {}), _jsx(Outlet, {})] }));
};
